import * as R from 'ramda';
import * as React from 'react';
import { useMemo, useEffect, FC, useCallback } from 'react';
import LocalDiv from '../localization/LocalDiv';
import { css } from '@emotion/core';
import LocalSelect from '../localization/LocalSelect';
import { useMsgText } from '../localization/hooks';
import { Str } from '../localization/translationKeys';
import Msg from '../localization/Msg';
import { FLEX_ROW, FLEX_COLUMN } from '../layout/flexBox';
import { COLORS } from '../globalStyle/colors';
import { SELECT } from '../forms/styles/select';
import Spacer from '../layout/Spacer';
import FilterBar from './FilterBar';
import { ReportRecipientParticipant, ReportRecipientProject, ReportRecipientReport } from './models';
import ReportRecipientReportList from './ReportRecipientReportList';
import { TextDirection } from '../localization/textDirection';
import { useToggle } from '../core/hooks/useToggle';
import ReportRecipientProjectList from './ReportRecipientProjectList';
import ClamShell from '../layout/ClamShell';
import ReportRecipientParticipantList from './ReportRecipientParticipantList';
import { useDispatch } from 'react-redux';
import { reportRecipientProjectsAction } from './api/reportRecipientProjectsAction';
import { reportRecipientParticipantsAction } from './api/reportRecipientParticipantsAction';
import { reportRecipientReportsAction } from './api/reportRecipientReportsAction';
import RecipientProjectCard from './RecipientProjectCard';
import RecipientParticipantCard from './RecipientParticipantCard';
import { useAppState } from '../core/redux/hooks';
import { toMap } from '../language/arrays';
import { setDateRangeAction } from './actions/setDateRangeAction';
import { setLastOnlyAction } from './actions/setLastOnlyAction';
import { addFilterProjectAction } from './actions/addFilterProjectAction';
import { removeFilterProjectAction } from './actions/removeFilterProjectAction';
import { addFilterParticipantAction } from './actions/addFilterParticipantAction';
import { removeFilterParticipantAction } from './actions/removeFilterParticipantAction';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useMount } from '../core/hooks/useMount';
import { isNilOrEmpty } from '../language/module';


const styles = {
  root:              css``,
  filterBar:         {
    root:           [
      FLEX_ROW,
      css`
        padding: 18px 30px;
        flex-wrap: wrap;
      `,
    ],
    icon:           css`
      cursor: pointer;
    `,
    dateRangeBar:   css`
      background-color: ${COLORS.LIGHT_GREY};
    `,
    projectBar:     css`
      background-color: ${COLORS.PROJECTBAR_GREY};
      border-top: 1px solid ${COLORS.WHITE};
      border-bottom: 1px solid ${COLORS.WHITE};
    `,
    participantBar: css`
      background-color: ${COLORS.PARTICIPANTBAR_GREY};
    `,
    dateContent:        css`
      display: flex;
      flex-direction: row;
      flex-basis: 92%; /* 100% causes content to wrap */
      justify-content: space-between;
    `,
    content:        css`
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
    `,
  },
  table:             css``,
  selectContainer:   {
    root:    FLEX_COLUMN,
    caption: css``,
    select:  SELECT,
  },
  checkBoxContainer: {
    root:     [
      FLEX_ROW,
      css`
        align-items: flex-start;
      `,
    ],
    checkBox: css``,
    label:    css`
      display: inline-block;
      max-width: 150px;
    `,
  },
  filterCaption: css`
    line-height: 28px;
  `,
  msgBar:           {
    root: css`
      background-color: ${COLORS.WHITE};
      padding: 0 30px;
      border: 0 none;
    `,
    icon: css`
      color: ${COLORS.BLUE};
      line-height: 50px;
      margin: auto 0;
    `,
    msgText: css`
      color: ${COLORS.DARK_GREY};
      background-color: transparent;
      line-height: 50px;
    `,
  },  
};


type Props = {
  className?: string;
  textDirection: TextDirection;
  recipientId: number;
  userName: string;
  hasDeliverPermission: boolean;
  deliverClicked: (report: ReportRecipientReport) => void;
  redeliverClicked: (report: ReportRecipientReport) => void;
  downloadClicked: () => void;
  downloadAllClicked: () => void;
  delivered: number;
  downloaded: number;
  messageVisible: boolean;
}


/**
 * ReportRecipientTable: a component that renders the report recipient's list of reports to be handled, as well as tools
 * to enable filtering the report list by project or participant
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ReportRecipientTable: FC<Props> = props => {
  const dispatch = useDispatch();

  const dateRange = useAppState(state => state.reportRecipientDateRange || 3);
  const setDateRange = useCallback(
    (range: number) => dispatch(setDateRangeAction(range)),
    [dispatch],
  );

  const projectArr = useAppState(state => state.reportRecipientProjects);
  const participantArr = useAppState(state => state.reportRecipientParticipants);

  const projects = React.useMemo(() => toMap(projectArr || [], proj => proj.displayName), [projectArr]);
  const participants = React.useMemo(() => toMap(participantArr || [], part => part.displayName), [participantArr]);
  const reports = useAppState(state => state.reportRecipientReports || []);

  const lastOnly = useAppState(state => state.reportRecipientLastOnly);
  const toggleLastOnly = useCallback(() => dispatch(setLastOnlyAction(!lastOnly)), [lastOnly, dispatch]);


  const [projectListVisible, showProjectList]         = useToggle();
  const [participantListVisible, showParticipantList] = useToggle();

  const stateFilterProjects = useAppState(state => state.reportRecipientSelectedProjects);
  const filterProjects      = useMemo(() => stateFilterProjects || {}, [stateFilterProjects]);
  const projectId = useAppState(state => state.auth.defaultRecipientProjectId);

  const addFilterProject = useCallback(
    (project: ReportRecipientProject) => {
      dispatch(addFilterProjectAction(project))
      showProjectList()
    },
    [showProjectList, dispatch],
  );

  const removeFilterProject = useCallback(
    (key: string) => dispatch(removeFilterProjectAction(key)),
    [dispatch],
  );

  useMount(
    () => {
      const project = Object.values(projects).find(
        proj => `${(proj.projectId)}` === projectId || proj.displayProjectId === projectId);

      if (!isNilOrEmpty(projectId) && Object.keys(filterProjects).length === 0 && project) {
       addFilterProject(project);
      }
    },
  );

  const stateFilterParticipants = useAppState(state => state.reportRecipientSelectedParticipants);
  const filterParticipants      = useMemo(() => stateFilterParticipants || {}, [stateFilterParticipants]);

  const addFilterParticipant = useCallback(
    (participant: ReportRecipientParticipant) => {
      dispatch(addFilterParticipantAction(participant))
      showParticipantList()
    },
    [showParticipantList, dispatch],
  );

  const removeFilterParticipant = useCallback(
    (key: string) => dispatch(removeFilterParticipantAction(key)),
    [dispatch],
  );

  const isProjectChecked = useCallback(
    (displayName: string) => !!filterProjects[displayName],
    [filterProjects],
  );

  const toggleCheckProject = useCallback(
    (displayName: string) =>
      !!filterProjects[displayName]
      ? removeFilterProject(displayName)
      : addFilterProject(projects[displayName]),
    [addFilterProject, filterProjects, projects, removeFilterProject],
  );

  const isParticipantChecked = useCallback(
    (participantIdString: string) => !!filterParticipants[participantIdString],
    [filterParticipants],
  );

  const toggleCheckParticipant = useCallback(
    (participantIdString: string) => !!filterParticipants[participantIdString]
      ? removeFilterParticipant(participantIdString)
      : addFilterParticipant(participants[participantIdString]),
    [addFilterParticipant, filterParticipants, participants, removeFilterParticipant],
  );

  const thisMonthStr     = useMsgText(Str.ThisMonth);
  const lastMonthStr     = useMsgText(Str.LastMonth);
  const last3MonthsStr   = useMsgText(Str.Last3Months);
  const last6MonthsStr   = useMsgText(Str.Last6Months);
  const thisYearStr      = useMsgText(Str.ThisYear);
  const lastYearStr      = useMsgText(Str.LastYear);
  const allDeliveriesStr = useMsgText(Str.AllDeliveries);

  const projectIds = useMemo(
    () => Object.values(filterProjects || {}).map(a => a.projectId),
    [filterProjects],
  );
  const participantIds = useMemo(
    () => Object.values(filterParticipants || {}).map(a => a.participantId),
    [filterParticipants],
  );

  useEffect(
    () => {
      dispatch(reportRecipientProjectsAction(props.recipientId, dateRange));
    },
    [props.recipientId, dateRange, dispatch],
  );

  useEffect(
    () => {
      dispatch(reportRecipientParticipantsAction(props.recipientId, projectIds, dateRange));
    },
    [props.recipientId, projectIds, dateRange, dispatch],
  );

  useEffect(
    () => {
      dispatch(reportRecipientReportsAction(
        props.recipientId,
        props.userName,
        projectIds,
        participantIds,
        dateRange,
        lastOnly));
    },
    [props.recipientId, props.userName, projectIds, participantIds, dateRange, lastOnly, dispatch],
  );

  const projectCards = useMemo(
    () => R.map(
      (project: ReportRecipientProject) => (
        <>
          <RecipientProjectCard
            key={project.displayName}
            project={project}
            onDelete={() => removeFilterProject(project.displayName)}
          />
          <Spacer sizePx={5} />
        </>
      ),
      Object.values(filterProjects ?? {}) || [],
    ),
    [filterProjects, removeFilterProject],
  );

  const participantCards = useMemo(
    () => R.map(
      (participant: ReportRecipientParticipant) => (
        <>
          <RecipientParticipantCard
            key={participant.displayName}
            participant={participant}
            onDelete={() => removeFilterParticipant(participant.displayName)}
          />
          <Spacer sizePx={5} />
        </>
      ),
      Object.values(filterParticipants ?? {}) || [],
    ),
    [filterParticipants, removeFilterParticipant],
  );

  const showProjects = useMemo(
    () => Object.values(filterProjects ?? {}).length > 0,
    [filterProjects],
  );

  const showParticipants = useMemo(
    () => Object.values(filterParticipants ?? {}).length > 0,
    [filterParticipants],
  );

  return (
    <LocalDiv className={props.className} css={styles.root}>

      <FilterBar
        css={[styles.filterBar.root, styles.filterBar.dateRangeBar]}
        contentStyle={styles.filterBar.dateContent}
        useDisabledIconColor={true}
      >
        <LocalDiv css={styles.selectContainer.root}>
          <Msg str={Str.DateRangeReports} css={styles.selectContainer.caption} />
          <Spacer sizePx={4} />
          <LocalSelect
            css={styles.selectContainer.select}
            onChange={event => setDateRange(+event.target.value)}
            value={dateRange}
          >
            <option key={1} value={1}>{thisMonthStr}</option>
            <option key={2} value={2}>{lastMonthStr}</option>
            <option key={3} value={3}>{last3MonthsStr}</option>
            <option key={4} value={4}>{last6MonthsStr}</option>
            <option key={5} value={5}>{thisYearStr}</option>
            <option key={6} value={6}>{lastYearStr}</option>
            <option key={7} value={7}>{allDeliveriesStr}</option>
          </LocalSelect>
        </LocalDiv>
        <LocalDiv css={styles.checkBoxContainer.root}>
          <input
            type='checkbox'
            css={styles.checkBoxContainer.checkBox}
            checked={lastOnly}
            onChange={toggleLastOnly}
          />
          <Spacer sizePx={12} />
          <Msg
            str={Str.ShowMostRecent}
            css={styles.checkBoxContainer.label}
            onClick={toggleLastOnly}
          />
        </LocalDiv>
      </FilterBar>

      <FilterBar
        css={[styles.filterBar.root, styles.filterBar.projectBar]}
        contentStyle={styles.filterBar.content}
        iconStyle={styles.filterBar.icon}
        useDarkIconColor={projectListVisible}
        onClickFilterIcon={showProjectList}
      >
        {!showProjects && <Msg str={Str.FilterProject} css={styles.filterCaption} /> }
        {filterProjects && projectCards}
      </FilterBar>

      <ClamShell show={!!projects && projectListVisible}>
        <ReportRecipientProjectList
          projects={Object.values(projects)}
          checkedProjects={filterProjects}
          isChecked={isProjectChecked}
          toggleCheck={toggleCheckProject}
        />
      </ClamShell>

      <FilterBar
        css={[styles.filterBar.root, styles.filterBar.participantBar]}
        contentStyle={styles.filterBar.content}
        iconStyle={styles.filterBar.icon}
        useDarkIconColor={participantListVisible}
        onClickFilterIcon={showParticipantList}
      >
        {!showParticipants && <Msg str={Str.FilterParticipant} css={styles.filterCaption} />}
        {filterParticipants && participantCards}
      </FilterBar>

      <ClamShell show={!!participants && participantListVisible}>
        <ReportRecipientParticipantList
          participants={Object.values(participants)}
          checkedParticipants={filterParticipants}
          isChecked={isParticipantChecked}
          toggleCheck={toggleCheckParticipant}
        />
      </ClamShell>

      <ClamShell
        show={props.messageVisible && (props.delivered > 0 || props.downloaded > 0)}
        css={[FLEX_ROW, styles.msgBar.root]}
      >
        <FontAwesomeIcon
          icon={faCheck}
          css={styles.msgBar.icon}
        />
        <Spacer sizePx={10} />
        <LocalDiv css={FLEX_COLUMN}>
          {
            <Msg str={props.delivered > 0 ? Str.ReportsDelivered : Str.ReportsDownloaded}
              replace={{ 'number': props.delivered > 0 ? props.delivered : props.downloaded }}
              css={styles.msgBar.msgText}
            />            
          }
        </LocalDiv>
      </ClamShell>      

      <ReportRecipientReportList
        reports={reports}
        textDirection={props.textDirection}
        downloadAllClicked={props.downloadAllClicked}
        hasDeliverPermission={props.hasDeliverPermission}
        deliverClicked={props.deliverClicked}
        redeliverClicked={props.redeliverClicked}
        downloadClicked={props.downloadClicked}
        recipientId={props.recipientId}
        dateRange={dateRange}
        projectIds={projectIds}
        participantIds={participantIds}
        lastOnly={lastOnly}
      />
    </LocalDiv>
  );
};

export default ReportRecipientTable;
